import React, { useState } from 'react'
import { gdprAPI } from '@hpanel/web-common/api/client'
import type { GdprErasure, GdprExport } from '@hpanel/web-common/api/types'

/**
 * Tenant Privacy page (Plan 9).
 *
 * Two flows:
 * - Article 20 export: synchronous; the panel returns a single-use
 *   download token (24h TTL). We open the download URL in a new tab.
 * - Article 17 erasure: tenant requests; admin must approve.
 *   Idempotent — filing twice returns the existing pending row.
 */
export const Privacy: React.FC = () => {
  const [busy, setBusy] = useState(false)
  const [exportRow, setExportRow] = useState<GdprExport | null>(null)
  const [erasureRow, setErasureRow] = useState<GdprErasure | null>(null)
  const [error, setError] = useState<string | null>(null)

  const requestExport = async () => {
    setBusy(true)
    setError(null)
    try {
      const row = await gdprAPI.requestExport()
      setExportRow(row)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Export failed')
    } finally {
      setBusy(false)
    }
  }

  const requestErasure = async () => {
    if (
      !window.confirm(
        "Request account erasure?\n\nThis files a request — it does NOT immediately delete anything. An administrator must approve before any data is removed.\n\nMake sure you've taken a data export FIRST if you want a portable copy.",
      )
    ) {
      return
    }
    setBusy(true)
    setError(null)
    try {
      const row = await gdprAPI.requestErasure()
      setErasureRow(row)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Erasure request failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div data-testid="privacy-page" className="space-y-6">
      <div>
        <h1 data-testid="page-title" className="text-3xl font-bold text-gray-900 dark:text-gray-100">
          Privacy
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Data export (GDPR Article 20) and account erasure (Article 17).
        </p>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-300 text-red-800 dark:text-red-200 rounded p-3">
          {error}
        </div>
      )}

      {/* Export */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
          Data export
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mt-2 text-sm">
          We assemble a ZIP containing your profile, sessions, containers,
          domains, DNS records, certificate metadata, snapshot metadata, audit
          log entries about you, and the contents of your{' '}
          <code>/home/&lt;username&gt;/</code> directory. Mailboxes and
          database dumps will be added in v2 — the manifest in the bundle
          documents what's pending and why.
        </p>

        <button
          onClick={requestExport}
          disabled={busy}
          data-testid="privacy-request-export-button"
          className="mt-4 px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white rounded-lg"
        >
          {busy ? 'Working…' : 'Request data export'}
        </button>

        {exportRow && (
          <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-300 rounded text-sm">
            <p className="font-semibold text-green-800 dark:text-green-200">
              Export ready (status: {exportRow.status})
            </p>
            <p className="mt-1 text-green-800 dark:text-green-200">
              Bundle size:{' '}
              {exportRow.bundle_size_bytes
                ? `${(exportRow.bundle_size_bytes / 1024).toFixed(1)} KB`
                : '—'}
              . Token expires:{' '}
              {exportRow.expires_at?.replace('T', ' ').slice(0, 16) ?? '—'}.
            </p>
            {exportRow.download_token && (
              <button
                type="button"
                onClick={async () => {
                  // F08-011: route the download through fetchAPIBlob
                  // with the token in a custom header, then trigger
                  // the browser-side save via an object-URL anchor.
                  // The token NEVER appears in the URL — closes the
                  // Referer / browser-history / proxy-log leak path.
                  try {
                    const { blob, filename } = await gdprAPI.download(
                      exportRow.id,
                      exportRow.download_token!,
                    );
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = filename;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                  } catch (e) {
                    // eslint-disable-next-line no-console
                    console.error('GDPR download failed', e);
                    alert((e as Error).message ?? 'Download failed');
                  }
                }}
                className="inline-block mt-3 px-3 py-1 bg-green-700 hover:bg-green-800 text-white rounded text-sm"
              >
                Download bundle
              </button>
            )}
          </div>
        )}
      </div>

      {/* Erasure */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
          Account erasure
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mt-2 text-sm">
          File a request to delete your account, container, files, domains,
          DNS records, certificates, mailboxes, snapshots, and sessions.{' '}
          <strong>An administrator must approve before anything is deleted</strong> —
          this gate exists so a stolen session can't trigger an instant
          irreversible delete. Audit-log entries are retained but anonymized.
        </p>

        <button
          onClick={requestErasure}
          disabled={busy}
          data-testid="privacy-request-erasure-button"
          className="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-lg"
        >
          {busy ? 'Working…' : 'Request account erasure'}
        </button>

        {erasureRow && (
          <div className="mt-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 rounded text-sm">
            <p className="text-yellow-800 dark:text-yellow-200">
              Request #{erasureRow.id} — status: <strong>{erasureRow.status}</strong>.
              {erasureRow.status === 'pending'
                ? ' Awaiting administrator approval.'
                : ''}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
